/**
 * HTTP Client
 *
 * Fetch wrapper with:
 * - Cookie-based authentication (credentials: 'include')
 * - JSON request/response handling
 * - Automatic error parsing
 * - Type-safe API
 */

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

export interface HttpError {
  error: string;
  code: string;
  statusCode: number;
  [key: string]: any;
}

export class HttpClientError extends Error {
  statusCode: number;
  code: string;
  details: Record<string, any>;

  constructor(statusCode: number, error: string, code: string, details: Record<string, any> = {}) {
    super(error);
    this.name = 'HttpClientError';
    this.statusCode = statusCode;
    this.code = code;
    this.details = details;
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: any;
  headers?: Record<string, string>;
  /** Set to false to skip sending credentials (default: true) */
  includeCredentials?: boolean;
  /**
   * Optional AbortSignal forwarded to fetch. Lets callers bind a fetch
   * to React Query's signal and/or chain it with AbortSignal.timeout(...)
   * so a hung endpoint can't trap a query in a perpetual pending state.
   * Additive: existing call sites that don't pass a signal are unaffected.
   */
  signal?: AbortSignal;
}

/**
 * Make HTTP request with automatic cookie handling
 */
async function request<T>(
  endpoint: string,
  options: RequestOptions = {}
): Promise<T> {
  const {
    method = 'GET',
    body,
    headers = {},
    includeCredentials = true,
    signal,
  } = options;

  const url = `${API_BASE_URL}${endpoint}`;

  // Only declare a JSON content type when we actually have a body to send.
  // Fastify's JSON parser rejects an empty body with 400 FST_ERR_CTP_EMPTY_JSON_BODY
  // when Content-Type: application/json is present (notably on DELETE).
  const hasBody = body !== undefined;
  const fetchOptions: RequestInit = {
    method,
    headers: {
      ...(hasBody ? { 'Content-Type': 'application/json' } : {}),
      ...headers,
    },
    credentials: includeCredentials ? 'include' : 'omit', // Send cookies
    ...(signal ? { signal } : {}),
  };

  if (hasBody) {
    fetchOptions.body = JSON.stringify(body);
  }

  try {
    const response = await fetch(url, fetchOptions);

    // Parse response body (always JSON from our API)
    let data: any;
    const contentType = response.headers.get('content-type');
    if (contentType?.includes('application/json')) {
      data = await response.json();
    } else {
      // Fallback for non-JSON responses
      const text = await response.text();
      data = { message: text };
    }

    // Handle errors
    if (!response.ok) {
      // Server flagged this user as needing to change their admin-issued
      // temp password before accessing anything else. Force-redirect so a
      // single detection point covers every route + hook; we never reach
      // the in-component error handler for this case. Safe as an additive
      // side effect: this branch only fires on the specific 403 code the
      // new middleware emits (see PASSWORD_CHANGE_REQUIRED in auth.middleware).
      if (
        response.status === 403 &&
        data?.code === 'PASSWORD_CHANGE_REQUIRED' &&
        typeof window !== 'undefined' &&
        window.location.pathname !== '/change-password'
      ) {
        window.location.href = '/change-password';
      }
      throw new HttpClientError(
        response.status,
        data.error || 'Request failed',
        data.code || 'UNKNOWN_ERROR',
        data
      );
    }

    return data as T;
  } catch (error) {
    // Re-throw HttpClientError as-is
    if (error instanceof HttpClientError) {
      throw error;
    }

    // Network errors or other fetch errors
    if (error instanceof Error) {
      throw new HttpClientError(
        0,
        error.message,
        'NETWORK_ERROR',
        { originalError: error }
      );
    }

    // Unknown errors
    throw new HttpClientError(
      0,
      'An unexpected error occurred',
      'UNKNOWN_ERROR',
      { originalError: error }
    );
  }
}

/**
 * HTTP client with cookie-based authentication
 */
export const httpClient = {
  /**
   * GET request
   */
  get<T>(endpoint: string, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'GET' });
  },

  /**
   * POST request
   */
  post<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'POST', body });
  },

  /**
   * PUT request
   */
  put<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PUT', body });
  },

  /**
   * PATCH request
   */
  patch<T>(endpoint: string, body?: any, options?: Omit<RequestOptions, 'method' | 'body'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'PATCH', body });
  },

  /**
   * DELETE request (optional JSON body via options.body, e.g. a preview token)
   */
  delete<T>(endpoint: string, options?: Omit<RequestOptions, 'method'>): Promise<T> {
    return request<T>(endpoint, { ...options, method: 'DELETE' });
  },
};
